import type {
  AcceptUserInvitationResult,
  AuthUser,
  ProviderDetail,
  ProviderInput,
  ProviderSummary,
  UserInvitationSummary
} from "@kuroo/contracts";
import type { AdminExport, AdminExportRequest, AdminImport, CatalogLocation } from "./client-catalog-types";
import type { TeamUser } from "./client-auth-types";

export type SdkAdminContext = {
  baseUrl: string;
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function inviteUser(context: SdkAdminContext, input: { email: string; firstName: string; lastName?: string; role: AuthUser["role"]; mfaRequired?: boolean }): Promise<UserInvitationSummary> {
  return context.request<UserInvitationSummary>("/api/v1/admin/users/invite", { method: "POST", body: JSON.stringify(input) });
}

export function revokeUserInvitation(context: SdkAdminContext, userId: string): Promise<{ userId: string; revoked: boolean }> {
  return context.request<{ userId: string; revoked: boolean }>(`/api/v1/admin/users/${encodeURIComponent(userId)}/invitation/revoke`, { method: "POST", body: "{}" });
}

export function acceptUserInvitation(context: SdkAdminContext, input: { token: string; password: string; confirmPassword: string }): Promise<AcceptUserInvitationResult> {
  return context.request<AcceptUserInvitationResult>("/api/v1/auth/invitations/accept", { method: "POST", body: JSON.stringify(input) });
}

export function createAdminExport(context: SdkAdminContext, input: AdminExportRequest = {}): Promise<{ id: string; format: "json" | "csv" | "zip"; scope: { entities?: string[] }; status: string }> {
  return context.request<{ id: string; format: "json" | "csv" | "zip"; scope: { entities?: string[] }; status: string }>("/api/v1/admin/exports", { method: "POST", body: JSON.stringify(input) });
}

export function adminExports(context: SdkAdminContext): Promise<AdminExport[]> {
  return context.request<AdminExport[]>("/api/v1/admin/exports");
}

export function adminExportDownloadUrl(context: SdkAdminContext, exportId: string): string {
  return `${context.baseUrl}/api/v1/admin/exports/${encodeURIComponent(exportId)}/download`;
}

export function adminImports(context: SdkAdminContext): Promise<AdminImport[]> {
  return context.request<AdminImport[]>("/api/v1/admin/imports");
}

export function users(context: SdkAdminContext): Promise<TeamUser[]> {
  return context.request<TeamUser[]>("/api/v1/admin/users");
}

export function createUser(context: SdkAdminContext, input: { email: string; firstName: string; lastName?: string; role?: AuthUser["role"]; password?: string }): Promise<TeamUser> {
  return context.request<TeamUser>("/api/v1/admin/users", { method: "POST", body: JSON.stringify(input) });
}

export function providersAdmin(context: SdkAdminContext, filters: { search?: string; status?: "active" | "inactive" } = {}): Promise<ProviderSummary[]> {
  const query = new URLSearchParams();
  if (filters.search) query.set("search", filters.search);
  if (filters.status) query.set("status", filters.status);
  return context.request<ProviderSummary[]>(`/api/v1/admin/providers${query.size ? `?${query}` : ""}`);
}

export function locationsAdmin(context: SdkAdminContext, filters: { status?: CatalogLocation["status"] } = {}): Promise<CatalogLocation[]> {
  const query = new URLSearchParams();
  if (filters.status) query.set("status", filters.status);
  return context.request<CatalogLocation[]>(`/api/v1/admin/locations${query.size ? `?${query}` : ""}`);
}

export function createProvider(context: SdkAdminContext, input: ProviderInput): Promise<ProviderDetail> {
  return context.request<ProviderDetail>("/api/v1/admin/providers", {
    method: "POST",
    body: JSON.stringify(input),
    headers: { "Idempotency-Key": `provider-create-${crypto.randomUUID()}` }
  });
}

export function provider(context: SdkAdminContext, providerId: string): Promise<ProviderDetail> {
  return context.request<ProviderDetail>(`/api/v1/admin/providers/${encodeURIComponent(providerId)}`);
}

export function updateProvider(context: SdkAdminContext, providerId: string, input: Partial<ProviderInput>): Promise<ProviderDetail> {
  return context.request<ProviderDetail>(`/api/v1/admin/providers/${encodeURIComponent(providerId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
    headers: { "Idempotency-Key": `provider-update-${providerId}-${crypto.randomUUID()}` }
  });
}

export function updateUser(context: SdkAdminContext, userId: string, input: { firstName?: string; lastName?: string; role?: AuthUser["role"]; status?: AuthUser["status"] }): Promise<Partial<TeamUser> & { id: string }> {
  return context.request<Partial<TeamUser> & { id: string }>(`/api/v1/admin/users/${encodeURIComponent(userId)}`, { method: "PATCH", body: JSON.stringify(input) });
}
